import React, { lazy, useContext, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { ErrorPage } from '@everfi/reactifi';
import { AppContext } from '@layout/Main';
import CustomFavIcon from '../lib/CustomFavIcon';
import { useAnalytics } from '@lib/analytics/useAnalytics';
import environment from '@/environment';

const ActionableDashboard = lazy(() => import('./ActionableDashboard/index'));
const Assignments = lazy(() => import('./Assignments/index'));
const Categories = lazy(() => import('../admin/Categories/index'));
const ContentLibrary = lazy(() => import('./ContentLibrary/index'));
const CustPortalReports = lazy(() => import('./CustPortalReports/index'));
const DataIntegration = lazy(() => import('../admin/DataIntegration/index'));
const Downloads = lazy(() => import('../admin/Downloads/index'));
const EmbedContentDomains = lazy(() => import('../admin/EmbedContentDomains/index'));
const FinancialAdminDashboard = lazy(() => import('./FinancialAdminDashboard/index'));
const IdentityProviders = lazy(() => import('./IdentityProviders/index'));
const Interstitial = lazy(() => import('./Interstitial/index'));
const Locations = lazy(() => import('./Locations/index'));
const OnboardingChecklist = lazy(() => import('./OnboardingChecklist/index'));
const OrgTemplate = lazy(() => import('./OrgTemplates/index'));
const Policies = lazy(() => import('../admin/Policies/index'));
const Resources = lazy(() => import('../admin/Resources/index'));
const Rosters = lazy(() => import('./Rosters/index'));
const ScormPackages = lazy(() => import('../admin/ScormContentPackages/index'));
const TagManagerComponent = lazy(() => import('./TagManager'));
const Teams = lazy(() => import('./Teams/index'));
const TrainingPeriods = lazy(() => import('../admin/TrainingPeriods/index'));
const UserUploads = lazy(() => import('./UserUploads/index'));
const Users = lazy(() => import('../admin/Users/index'));
const Webhooks = lazy(() => import('../admin/Webhooks/index'));

function Cportal() {
  const context = useContext(AppContext);
  const { currentRuleSet, currentRuleSetName, organization, user } = context;

  useAnalytics({
    userId: user?.id,
    accountId: organization?.id,
    apiKey: environment.PENDO_ANALYTICS_API_KEY_FOUNDRY,
    isFoundry: true,
  });

  useEffect(() => {
    if (user && organization) {
      const tagManagerArgs = {
        dataLayer: {
          user_id: user.id,
          user_is_impersonated: !!user.impersonator_id,
          user_rule_set: currentRuleSet?.name || currentRuleSetName,
          org_id: organization.id,
          org_has_elective_invites: organization.capability_list.includes('Capabilities::LearningInvitation'),
          org_has_legacy_assignments: organization.capability_list.includes('Capabilities::LegacyAssignments'),
          org_is_demo: organization.demo,
        },
        dataLayerName: 'globalDataLayer',
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  }, [currentRuleSet, currentRuleSetName, organization, user]);

  const isAdmin = useMemo(() => {
    if (!user?.rule_sets) {
      return false;
    }

    return user.rule_sets.some(ruleSet =>
      [
        'cc_admin',
        'he_admin',
        'fac_staff_admin',
        'at_work_manager',
        'at_work_advisor',
        'scorm_manager',
        'administrator_curriculum_coordinator',
      ].includes(ruleSet.name)
    );
  }, [user]);

  const dashboardUrl = `/${organization?.slug}/cportal/actionable_dashboard`;

  return (
    <>
      <CustomFavIcon />
      <Routes>
        <Route path="/cportal">
          {isAdmin && (
            <>
              <Route path="actionable_dashboard/*" element={<ActionableDashboard />} />
              <Route path="reports_home/*" element={<Navigate replace to={dashboardUrl} />} />
              <Route path="" element={<Navigate replace to={dashboardUrl} />} />
              <Route path="assignments/*" element={<Assignments />} />
              <Route path="categories/*" element={<Categories />} />
              <Route path="checklist/*" element={<OnboardingChecklist />} />
              <Route path="content_library/*" element={<ContentLibrary />} />
              <Route path="data_integration/*" element={<DataIntegration />} />
              <Route path="downloads/*" element={<Downloads />} />
              <Route path="embed_content/*" element={<EmbedContentDomains />} />
              <Route path="financial_launchpad/*" element={<FinancialAdminDashboard />} />
              <Route path="identity_providers/*" element={<IdentityProviders />} />
              <Route path="interstitial_redirect/*" element={<Interstitial />} />
              <Route path="locations/*" element={<Locations />} />
              <Route path="policies/*" element={<Policies />} />
              <Route path="reports/*" element={<CustPortalReports />} />
              <Route path="resources/*" element={<Resources />} />
              <Route path="rosters/*" element={<Rosters />} />
              <Route path="scorm_packages/*" element={<ScormPackages />} />
              <Route path="tag_manager/*" element={<TagManagerComponent />} />
              <Route path="teams/*" element={<Teams />} />
              <Route path="templates/*" element={<OrgTemplate />} />
              <Route path="training_periods/*" element={<TrainingPeriods />} />
              <Route path="user_uploads/*" element={<UserUploads />} />
              <Route path="users/*" element={<Users />} />
              <Route path="webhooks/*" element={<Webhooks />} />
            </>
          )}
          <Route path="*" element={<ErrorPage title="Page Not Found" />} />
        </Route>
      </Routes>
    </>
  );
}

export default Cportal;
